import React, {
  FC, RefObject, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import cx from 'clsx';
import { Icon } from '@mdi/react';
import moment from 'moment';
import { useSelector, useStore } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocale } from 'lib';
import {
  ModalTitle, Overlay, useAnimatedOverlay, useDraggableOverlay,
} from 'components';
import { stateSelector } from 'features/details/chat';
import { downloadAlarmExport } from 'features/details/exportAlarm';
import { RS } from 'types';
import {
  alarmLogActor, alarmLogDetail, alarmLogIcon, alarmLogTitle, operatorDisplayNames,
} from 'features/details/chat/formatLog';
import { Shadow } from '../shadow';
import styles from './style.module.scss';

const previewCount = 4;

const formatLogTime = (createdAt: string): string => {

  const value = moment(createdAt);

  if (value.isSame(moment(), 'day')) return value.format('HH:mm');

  return value.format('DD-MM-YYYY HH:mm');

};

export const Logs: FC<{ containerRef: RefObject<HTMLDivElement> }> = ({ containerRef }) => {

  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const { shouldRender, overlayProps } = useAnimatedOverlay(open);
  const store = useStore<RS>();
  const [exporting, setExporting] = useState(false);
  const draggableProps = useDraggableOverlay({ containerRef, handleSelector: '[data-drag-handle]' });
  const { logs, operatorMessages, status } = useSelector(stateSelector);
  const operatorNames = operatorDisplayNames(
    [...operatorMessages, ...logs],
    t('Alarm_Detail_Logs_Operator'),
    (number) => t('Alarm_Detail_Logs_OperatorNumber', { number }),
  );
  const seenIds = useRef<Set<string> | null>(null);
  const [freshIds, setFreshIds] = useState<ReadonlySet<string>>(() => new Set());
  const preview = logs.slice(0, previewCount);

  useEffect(() => {

    if (!status.fulfilled) return;

    const ids = logs.map((event) => event._id);

    if (!seenIds.current) {

      seenIds.current = new Set(ids);
      return;

    }

    const arrived = ids.filter((id) => !seenIds.current?.has(id));

    ids.forEach((id) => seenIds.current?.add(id));

    if (arrived.length === 0) return;

    setFreshIds((current) => new Set([...current, ...arrived]));

  }, [logs, status]);

  const rows = (items: typeof logs) => items.map((event) => {

    const detail = alarmLogDetail(event);

    return (
      <div key={event._id} className={cx(styles.row, freshIds.has(event._id) && styles.fresh)}>
        <Icon path={alarmLogIcon(event)} size="16px"/>
        <div className={styles.body}>
          <div className={styles.line}>
            <span className={styles.actor}>{alarmLogActor(event, t, operatorNames)}</span>
            <time className={styles.time}>{formatLogTime(event.createdAt)}</time>
          </div>
          <div className={styles.title}>{alarmLogTitle(event, t)}</div>
          {detail && <div className={styles.detail}>{detail}</div>}
        </div>
      </div>
    );

  });

  const exportAlarm = async () => {

    setExporting(true);

    try {

      await downloadAlarmExport(store.getState(), t);

    } catch {

      toast.error(t('Alarm_Detail_Export.Failed'));

    } finally {

      setExporting(false);

    }

  };

  // Exporting while these are still loading would write them as empty.
  const loading = useSelector((state: RS) => {

    const { helpers, response, chat } = state.details;

    return [chat, helpers, response.static, response.dynamic].some((slice) => slice.status.pending);

  });

  const exportButton = (
    <button type="button" className={styles.export} disabled={exporting || loading} onClick={exportAlarm}>
      {t('Alarm_Detail_Export.Button')}
    </button>
  );

  const modal = shouldRender && containerRef.current ? createPortal(
    <>
      <Shadow enabled={open}/>
      <Overlay
        onDismiss={() => setOpen(false)}
        {...draggableProps}
        {...overlayProps({ modal: true, centered: false, className: styles.overlay })}
      >
        <ModalTitle dragHandle text={t('Alarm_Detail_Logs_FullTitle')} onClose={() => setOpen(false)}/>
        <div className={styles.full}>{rows(logs)}</div>
        <div className={styles.footer}>{exportButton}</div>
      </Overlay>
    </>,
    containerRef.current,
  ) : null;

  return (
    <section className={styles.box}>
      <div className={styles.blockTitleContainer}>{t('Alarm_Detail_Logs_Title')}</div>
      <div className={styles.list}>
        {preview.length > 0 && rows(preview)}
        {preview.length === 0 && status.fulfilled && (
          <p className={styles.empty}>{t('Alarm_Detail_Logs_Empty')}</p>
        )}
      </div>
      {logs.length > previewCount ? (
        <button type="button" className={styles.more} onClick={() => setOpen(true)}>
          {t('Alarm_Detail_Logs_ShowMore')}
        </button>
      ) : <div className={styles.footer}>{exportButton}</div>}
      {modal}
    </section>
  );

};
